import { useLocation, useNavigate } from 'react-router-dom';

import { usePageVersion } from '@/hooks/usePageVersion';

const HOLDINGS_TAB = { value: 'holdings', label: 'Holdings', legacyValue: 'positions' } as const;
const TRANSACTIONS_TAB = { value: 'transactions', label: 'Transactions', legacyValue: 'transactions/all' } as const;

export const ACCOUNT_PAGE_TABS = [
    { value: 'account-overview', label: 'Overview', legacyValue: 'account-overview' },
    { value: 'investment-policy', label: 'Investment Policy', legacyValue: 'allocations' },
    HOLDINGS_TAB,
    { value: 'trades', label: 'Trades', legacyValue: 'trades' },
    TRANSACTIONS_TAB,
] as const;

const trimTrailingSlash = (pathname: string) => pathname.replace(/\/+$/, '');

export const matchAccountPageTab = (rawPathname: string) => {
    const pathname = trimTrailingSlash(rawPathname);
    const holdings = pathname.match(/\/(?:holdings\/)?(positions|lots\/(?:open|closed))$/);
    if (holdings) {
        return {
            ...HOLDINGS_TAB,
            routeValue: `holdings/${holdings[1] === 'lots/closed' ? 'lots/open' : holdings[1]}`,
            legacyValue: holdings[1],
        };
    }
    const transactions = pathname.match(/\/transactions(?:\/(all|cash|transfers))?$/);
    if (transactions) {
        return {
            ...TRANSACTIONS_TAB,
            routeValue: `transactions/${transactions[1] ?? 'all'}`,
            legacyValue: `transactions/${transactions[1] ?? 'all'}`,
        };
    }
    const tab = ACCOUNT_PAGE_TABS.find(
        (candidate) => pathname.endsWith(`/${candidate.value}`) || pathname.endsWith(`/${candidate.legacyValue}`),
    );
    return tab ? { ...tab, routeValue: tab.value === 'holdings' ? 'holdings/positions' : tab.value } : undefined;
};

export const accountPageBase = (pathname: string) =>
    trimTrailingSlash(pathname).replace(
        /\/(account-overview|investment-policy|allocations|holdings(?:\/.*)?|positions|lots(?:\/.*)?|trades|transactions(?:\/.*)?)$/,
        '',
    );

export const useAccountPageView = () => {
    const preference = usePageVersion();
    const location = useLocation();
    const navigate = useNavigate();
    const hasNewTab = !!matchAccountPageTab(location.pathname);
    // Legacy-only deep links remain usable without changing the shared preference.
    const view = hasNewTab ? preference.view : 'legacy';

    const switchView = () => {
        const next = view === 'new' ? 'legacy' : 'new';
        preference.setView(next);
        if (next === 'new' && !hasNewTab)
            navigate(`${accountPageBase(location.pathname)}/${ACCOUNT_PAGE_TABS[0].value}`);
    };

    return { view, enabled: preference.enabled, isPending: preference.isPending, switchView };
};
